// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, MapPin } from 'lucide-react';
import { toast } from 'react-toastify';
import { fetchHorariosPublic, fetchRestaurantSettingsPublic, fetchBloqueiosPublic, BloqueioResponse } from '../../services/api.ts';
import { checkPublicAvailability } from '../../../../../services/publicReservoApi';
import { formatDateToLocalISO, getReservoDayOfWeek } from '../../utils/date.ts';
import '../../styles/date-time-picker.css';

interface DateTimePickerProps {
  value: {
    date: Date | null;
    time: string;
    guests: number;
  };
  restaurantSlug: string;
  onChange: (data: Partial<{ date: Date | null; time: string; guests: number }>) => void;
  onNext: () => void;
  showEnvironmentSelector?: boolean;
  environments?: Array<{ id: string; name: string }>;
  selectedEnvironmentId?: string;
  onEnvironmentChange?: (id: string) => void;
  guestLimits?: {
    min?: number;
    max?: number;
  };
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
  value,
  restaurantSlug,
  onChange,
  onNext,
  showEnvironmentSelector = false,
  environments = [],
  selectedEnvironmentId = '',
  onEnvironmentChange,
  guestLimits
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [timeEnabled, setTimeEnabled] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);
  const [guestWarning, setGuestWarning] = useState('');
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [noTimesMessage, setNoTimesMessage] = useState('');
  const [restaurantSettings, setRestaurantSettings] = useState<any>(null);

  const collectEnvironmentIds = () => {
    const ids: string[] = [];

    if (selectedEnvironmentId) {
      ids.push(String(selectedEnvironmentId));
    }

    if (Array.isArray(environments) && environments.length > 0) {
      environments.forEach(env => {
        if (!env) return;
        const value = (env as any).id;
        if (value === null || value === undefined || value === '') return;
        ids.push(String(value));
      });
    }

    return Array.from(new Set(ids));
  };

  const resolveScopedEnvironmentIds = () => {
    if (showEnvironmentSelector && selectedEnvironmentId) {
      return [String(selectedEnvironmentId)];
    }

    return collectEnvironmentIds();
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await fetchRestaurantSettingsPublic(restaurantSlug);
        setRestaurantSettings(data || {});
      } catch (error) {
        console.error('Erro ao carregar configurações públicas:', error);
      }
    };

    if (restaurantSlug) {
      loadSettings();
    }
  }, [restaurantSlug]);

  const allowedMin = guestLimits?.min ?? 1;
  const allowedMax = guestLimits?.max ?? 20;

  const maxAdvanceDays = React.useMemo(() => {
    const raw = restaurantSettings?.reservo_prazo_reserva_dias;
    const parsed = raw !== null && raw !== undefined && raw !== ''
      ? Number.parseInt(String(raw), 10)
      : NaN;
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [restaurantSettings]);

  const maxDateValue = React.useMemo(() => {
    if (!maxAdvanceDays) return undefined;
    const maxDate = new Date();
    maxDate.setHours(0, 0, 0, 0);
    maxDate.setDate(maxDate.getDate() + maxAdvanceDays);
    return formatDateToLocalISO(maxDate);
  }, [maxAdvanceDays]);

  const handleEnvironmentSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setGuestWarning('');
    setNoTimesMessage('');
    onEnvironmentChange?.(e.target.value);
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    setNoTimesMessage('');
    if (!inputValue) {
      setTimeEnabled(false);
      setAvailableTimes([]);
      onChange({ date: null, time: '' });
      return;
    }

    const newDate = new Date(inputValue + 'T00:00:00');
    newDate.setHours(0, 0, 0, 0);
    onChange({ date: newDate, time: '' });
    setTimeEnabled(true);
    setAvailableTimes([]);
    setGuestWarning('');
    setNoTimesMessage('');
    fetchAvailableTimes(newDate);
  };

  const fetchAvailableTimes = async (selectedDate: Date) => {
    try {
      setLoadingTimes(true);
      setNoTimesMessage('');

      const horarios = await fetchHorariosPublic(restaurantSlug);
      const dayOfWeek = getReservoDayOfWeek(selectedDate);
      const dayHorarios = horarios.filter((h: any) => h.dia_semana === dayOfWeek && h.aberto);
      if (dayHorarios.length === 0) {
        setAvailableTimes([]);
        setNoTimesMessage('Não existem horários disponíveis para esta data.');
        return;
      }

      const settingsData = restaurantSettings || await fetchRestaurantSettingsPublic(restaurantSlug);
      if (!restaurantSettings) {
        setRestaurantSettings(settingsData || {});
      }
      const intervaloMinutos = settingsData?.reservo_intervalo_minutos || 30;

      let dayBlocks: BloqueioResponse[] = [];
      try {
        const bloqueios = await fetchBloqueiosPublic(restaurantSlug);
        const targetKey = formatDateToLocalISO(selectedDate);
        dayBlocks = (bloqueios || []).filter(block => block && block.data === targetKey);
      } catch (blockError) {
        console.error('Erro ao buscar bloqueios públicos:', blockError);
      }

      const blockCoversSlot = (block: BloqueioResponse, turnoNumber: number, horarioId: number) => {
        const blockTurno = block.turno !== null && block.turno !== undefined ? Number(block.turno) : null;
        const blockHorario = block.horario_id !== null && block.horario_id !== undefined ? Number(block.horario_id) : null;
        const matchesHorario = blockHorario !== null && blockHorario === horarioId;
        const matchesTurno = blockTurno !== null && blockTurno === turnoNumber;
        const isGeneral = blockHorario === null && blockTurno === null;
        return matchesHorario || matchesTurno || isGeneral;
      };

      const uniqueEnvironmentIds = resolveScopedEnvironmentIds();

      const isSlotGloballyBlocked = (turnoNumber: number, horarioId: number) => {
        return dayBlocks.some(block => {
          if (!blockCoversSlot(block, turnoNumber, horarioId)) {
            return false;
          }
          return block.sala_id === null || block.sala_id === undefined;
        });
      };

      const isSlotBlockedForSala = (salaId: string, turnoNumber: number, horarioId: number) => {
        return dayBlocks.some(block => {
          if (!blockCoversSlot(block, turnoNumber, horarioId)) {
            return false;
          }

          if (block.sala_id === null || block.sala_id === undefined) {
            return true;
          }

          return String(block.sala_id) === salaId;
        });
      };

      const hasBlockingImpact = dayBlocks.some(block =>
        dayHorarios.some((horario: any) => {
          const horarioId = Number(horario.id);
          const turnoNumber = Number(horario.turno);
          if (Number.isNaN(horarioId) || Number.isNaN(turnoNumber)) {
            return false;
          }
          return blockCoversSlot(block, turnoNumber, horarioId);
        })
      );

      const usableHorarios = dayHorarios.filter((horario: any) => {
        const horarioId = Number(horario.id);
        const turnoNumber = Number(horario.turno);

        if (Number.isNaN(horarioId) || Number.isNaN(turnoNumber)) {
          return true;
        }

        if (isSlotGloballyBlocked(turnoNumber, horarioId)) {
          return false;
        }

        if (uniqueEnvironmentIds.length === 0) {
          return true;
        }

        const allSalasIndisponiveis = uniqueEnvironmentIds.every(salaId =>
          isSlotBlockedForSala(salaId, turnoNumber, horarioId)
        );

        return !allSalasIndisponiveis;
      });

      if (usableHorarios.length === 0) {
        const hasBlocks = hasBlockingImpact;
        setAvailableTimes([]);
        setNoTimesMessage(
          hasBlocks
            ? 'Todos os horários estão bloqueados para esta data. Não existem horários disponíveis.'
            : 'Não existem horários disponíveis para esta data.'
        );
        return;
      }

      const allTimes: string[] = [];
      usableHorarios.forEach((horario: any) => {
        const times = generateTimeSlots(horario.hora_inicio, horario.hora_fim, intervaloMinutos);
        allTimes.push(...times);
      });

      const uniqueTimes = [...new Set(allTimes)].sort();

      setAvailableTimes(uniqueTimes);
      setNoTimesMessage('');
    } catch (error) {
      console.error('Erro ao buscar horários disponíveis:', error);
      setAvailableTimes([]);
      setNoTimesMessage('Não foi possível carregar horários disponíveis.');
    } finally {
      setLoadingTimes(false);
    }
  };

  const generateTimeSlots = (start: string, end: string, interval: number): string[] => {
    const times: string[] = [];
    const startMinutes = timeToMinutes(start);
    const endMinutes = timeToMinutes(end);

    if (Number.isNaN(startMinutes) || Number.isNaN(endMinutes) || interval <= 0) {
      return times;
    }

    for (let minutes = startMinutes; minutes < endMinutes; minutes += interval) {
      times.push(minutesToTime(minutes));
    }

    if (endMinutes >= startMinutes) {
      const endTime = minutesToTime(endMinutes);
      if (!times.includes(endTime)) {
        times.push(endTime);
      }
    }

    return times;
  };

  const timeToMinutes = (timeString: string): number => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const minutesToTime = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const applyGuestChange = (targetValue: number) => {
    if (Number.isNaN(targetValue)) {
      return;
    }

    let newValue = targetValue;
    let warning = '';

    if (targetValue < allowedMin) {
      newValue = allowedMin;
      warning = `Número mínimo de pessoas: ${allowedMin}`;
    } else if (targetValue > allowedMax) {
      newValue = allowedMax;
      warning = `Número máximo de pessoas: ${allowedMax}`;
    }

    setGuestWarning(warning);

    if (value.guests !== newValue) {
      onChange({ guests: newValue });
    }
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ time: e.target.value });
  };

  const handleGuestsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    applyGuestChange(parseInt(e.target.value, 10));
  };

  const incrementGuests = () => {
    applyGuestChange(value.guests + 1);
  };

  const decrementGuests = () => {
    applyGuestChange(value.guests - 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      incrementGuests();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      decrementGuests();
    }
  };

  const validateStep = () => {
    if (showEnvironmentSelector && !selectedEnvironmentId) {
      toast.warning('Selecione um ambiente para continuar.');
      return false;
    }

    if (!value.date) {
      toast.warning('Selecione uma data para continuar.');
      return false;
    }

    if (!value.time) {
      toast.warning('Selecione um horário disponível.');
      return false;
    }

    if (Number.isNaN(value.guests)) {
      toast.warning('Informe o número de pessoas.');
      return false;
    }

    if (value.guests < allowedMin) {
      toast.warning(`Número mínimo de pessoas: ${allowedMin}`);
      return false;
    }

    if (value.guests > allowedMax) {
      toast.warning(`Número máximo de pessoas: ${allowedMax}`);
      return false;
    }

    if (timeEnabled && availableTimes.length === 0) {
      toast.warning('Nenhum horário disponível para a data selecionada.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep()) {
      return;
    }

    if (!value.date || !value.time) {
      toast.error('Escolha uma data e horário válidos.');
      return;
    }

    const candidateSalaIds = resolveScopedEnvironmentIds();
    if (!candidateSalaIds.length) {
      toast.error('Não foi possível identificar o ambiente da reserva.');
      return;
    }

    try {
      setCheckingAvailability(true);

      let hasAvailability = false;
      let lastReason: string | undefined;

      for (const salaId of candidateSalaIds) {
        const availability = await checkPublicAvailability(restaurantSlug, {
          sala_id: salaId,
          data: formatDateToLocalISO(value.date),
          hora: value.time,
          numero_pessoas: value.guests,
        });

        if (availability?.available) {
          hasAvailability = true;
          break;
        }

        if (availability?.reason) {
          lastReason = availability.reason;
        }
      }

      if (!hasAvailability) {
        toast.error(lastReason || 'Capacidade esgotada para este horário.');
        return;
      }

      onNext();
    } catch (error: any) {
      console.error('Erro ao verificar disponibilidade:', error);
      const backendMessage = error?.response?.data?.error;
      toast.error(backendMessage || 'Não foi possível verificar a disponibilidade. Tente novamente.');
    } finally {
      setCheckingAvailability(false);
    }
  };

  return (
    <form className="date-time-picker" onSubmit={handleSubmit} noValidate>
      {showEnvironmentSelector && (
        <div className="picker-field environment-picker">
          <div className="picker-label">
            <MapPin size={20} />
            <span>Ambiente</span>
          </div>
          <select
            className="picker-input"
            value={selectedEnvironmentId}
            onChange={handleEnvironmentSelect}
            disabled={!environments.length}
            aria-label="Selecione um ambiente"
          >
            {!selectedEnvironmentId && <option value="" disabled>Selecione um ambiente</option>}
            {environments.map(env => (
              <option key={env.id} value={env.id}>{env.name}</option>
            ))}
          </select>
        </div>
      )}

      <div className="picker-field date-picker">
        <div className="picker-label">
          <Calendar size={20} />
          <span>Data</span>
        </div>
        <input
          className="picker-input"
          type="date"
          lang="pt-BR"
          value={formatDateToLocalISO(value.date)}
          onChange={handleDateChange}
          min={formatDateToLocalISO(new Date())}
          max={maxDateValue}
          required
        />
      </div>

      <div className="picker-field time-picker">
        <div className="picker-label">
          <Clock size={20} />
          <span>Horário</span>
          {loadingTimes && <span className="inline-hint">Carregando...</span>}
        </div>
        <select
          className="picker-input"
          value={value.time}
          onChange={handleTimeChange}
          disabled={!timeEnabled || loadingTimes}
          required
        >
          {!timeEnabled && (
            <option value="">Selecione uma data primeiro</option>
          )}

          {timeEnabled && loadingTimes && (
            <option value="">Carregando horários...</option>
          )}

          {timeEnabled && !loadingTimes && availableTimes.length === 0 && (
            <option value="">Nenhum horário disponível</option>
          )}

          {timeEnabled && !loadingTimes && availableTimes.length > 0 && ([
            <option key="placeholder" value="">Selecione um horário</option>,
            ...availableTimes.map(time => (
              <option key={time} value={time}>{time}</option>
            ))
          ])}
        </select>
        {noTimesMessage && (
          <div className="picker-helper warning">{noTimesMessage}</div>
        )}
      </div>

      <div className="picker-field guests-picker">
        <div className="picker-label">
          <Users size={20} />
          <span>Pessoas</span>
        </div>
        <div className="guests-input-container">
          <input
            className="picker-input"
            type="number"
            min={allowedMin}
            max={allowedMax}
            value={value.guests}
            onChange={handleGuestsChange}
            onKeyDown={handleKeyDown}
            aria-label="Número de pessoas"
            aria-describedby="guests-help"
            required
          />
          {isMobile && (
            <div className="guests-buttons">
              <button
                type="button"
                className="guests-btn guests-btn-up"
                onClick={incrementGuests}
                aria-label="Aumentar número de pessoas"
              >
                +
              </button>
              <button
                type="button"
                className="guests-btn guests-btn-down"
                onClick={decrementGuests}
                aria-label="Diminuir número de pessoas"
              >
                -
              </button>
            </div>
          )}
        </div>
        {guestLimits && (
          <div className="picker-helper">
            Min: {guestLimits.min ?? allowedMin} - Max: {guestLimits.max ?? allowedMax}
          </div>
        )}
        {guestWarning && (
          <div className="picker-helper warning">
            {guestWarning}
          </div>
        )}
        <div id="guests-help" className="sr-only">
          Use as setas do teclado ou os botões para ajustar o número de pessoas
        </div>
      </div>

      <div className="date-time-actions">
        <button className="btn-next" type="submit" disabled={checkingAvailability}>
          {checkingAvailability ? 'Verificando...' : 'Avançar'}
        </button>
      </div>
    </form>
  );
};

export default DateTimePicker;
